import axios from 'axios';

// Use relative path '/api' to leverage Vite proxy in development
// and same-origin API in production
const API_BASE_URL = import.meta.env.VITE_API_URL ||
  (import.meta.env.DEV ? '/api' : 'https://hamrokrishibazzar-backend.vercel.app/api');

// Create underlying HTTP client
const httpClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
httpClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Suppress 401 errors for auth endpoints (expected when not logged in)
httpClient.interceptors.response.use(
  (response) => response,
  (error) => {
    // Silently handle 401 errors for /auth/me - these are expected when not logged in
    if (error.response?.status === 401 && error.config?.url?.includes('/auth/me')) {
      // Suppress console error for expected 401s
      // The error is still rejected so calling code can handle it, but we don't log it
      error.suppressLog = true;
      return Promise.reject(error);
    }
    // Log other errors (but not 401s)
    if (error.response?.status !== 401 && !error.suppressLog) {
      console.error('API Error:', error.response?.data || error.message);
    }
    return Promise.reject(error);
  }
);

// Generic entity operations factory
const createEntityAPI = (entityName) => ({
  list: async (sort = '-created_at', limit = 50) => {
    const res = await httpClient.get(`/${entityName}`, { params: { sort, limit } });
    return res.data;
  },

  filter: async (query = {}, sort = '-created_at', limit = 50) => {
    const res = await httpClient.get(`/${entityName}`, { params: { ...query, sort, limit } });
    return res.data;
  },

  create: async (data) => {
    const res = await httpClient.post(`/${entityName}`, data);
    return res.data;
  },

  update: async (id, data) => {
    const res = await httpClient.put(`/${entityName}/${id}`, data);
    return res.data;
  },

  delete: async (id) => {
    const res = await httpClient.delete(`/${entityName}/${id}`);
    return res.data;
  },

  schema: async () => {
    const res = await httpClient.get(`/${entityName}/schema`);
    return res.data;
  },
});

// Main API client export
export const apiClient = {
  // Authentication
  auth: {
    me: async () => {
      try {
        const res = await httpClient.get('/auth/me');
        return res.data;
      } catch (error) {
        // Return null for 401 errors (not logged in) - this is expected
        if (error.response?.status === 401) {
          // Do not auto-delete token on 401. This causes race conditions during login.
          // localStorage.removeItem('auth_token');
          // localStorage.removeItem('user');
          return null;
        }
        throw error;
      }
    },

    logout: (redirectUrl) => {
      localStorage.removeItem('auth_token');
      window.location.href = redirectUrl || '/';
    },

    redirectToLogin: (nextUrl) => {
      window.location.href = `/login?next=${encodeURIComponent(nextUrl || window.location.pathname)}`;
    },

    updateMe: async (data) => {
      const res = await httpClient.put('/auth/me', data);
      return res.data;
    },

    isAuthenticated: async () => {
      try {
        await httpClient.get('/auth/me');
        return true;
      } catch {
        return false;
      }
    },
  },

  // Entities
  entities: {
    User: createEntityAPI('users'),
    Farmer: createEntityAPI('farmers'),
    Product: createEntityAPI('products'),
    Order: createEntityAPI('orders'),
    Cart: createEntityAPI('carts'),
    Category: createEntityAPI('categories'),
    Transaction: createEntityAPI('transactions'),
    ActivityLog: createEntityAPI('activity-logs'),
    BuyerSubscription: createEntityAPI('buyer-subscriptions'),
    FarmerSubscriptionPlan: createEntityAPI('farmer-subscription-plans'),
    SubscriptionPayment: createEntityAPI('subscription-payments'),
    Delivery: createEntityAPI('deliveries'),
    Driver: createEntityAPI('drivers'),
    Rating: createEntityAPI('ratings'),
    FarmerLoyalty: createEntityAPI('farmer-loyalties'),
    SupportTicket: createEntityAPI('support-tickets'),
    SupportMessage: createEntityAPI('support-messages'),
    Dispute: createEntityAPI('disputes'),
    BuyerProtection: createEntityAPI('buyer-protections'),
    Role: createEntityAPI('roles'),
    Message: createEntityAPI('messages'),
    Inventory: createEntityAPI('inventories'),
    PriceHistory: createEntityAPI('price-histories'),
    Wishlist: createEntityAPI('wishlists'),
    Review: createEntityAPI('reviews'),
    SavedAddress: createEntityAPI('saved-addresses'),
    PayoutReport: createEntityAPI('payout-reports'),
    DriverCommission: createEntityAPI('driver-commissions'),
    DriverWithdrawal: createEntityAPI('driver-withdrawals'),
    Referral: createEntityAPI('referrals'),
    ReferralCommission: createEntityAPI('referral-commissions'),
    ReferralConfig: createEntityAPI('referral-configs'),
    UserReferral: createEntityAPI('user-referrals'),
    ReferralTracking: createEntityAPI('referral-tracking'),
    UserWallet: createEntityAPI('user-wallets'),
    MarketingAgent: createEntityAPI('marketing-agents'),
    DeliveryRating: createEntityAPI('delivery-ratings'),
    SubscriptionTransaction: createEntityAPI('subscription-transactions'),
    Announcement: createEntityAPI('announcements'),
    NewsletterSubscriber: createEntityAPI('newsletter-subscribers'),
  },

  // Integrations
  integrations: {
    Core: {
      UploadFile: async ({ file }) => {
        const formData = new FormData();
        formData.append('file', file);
        const res = await httpClient.post('/upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        return res.data;
      },

      SendEmail: async ({ to, subject, body, from_name }) => {
        const res = await httpClient.post('/email/send', { to, subject, body, from_name });
        return res.data;
      },

      InvokeLLM: async ({ prompt, response_json_schema, add_context_from_internet }) => {
        const res = await httpClient.post('/ai/invoke', {
          prompt,
          response_json_schema,
          add_context_from_internet
        });
        return res.data;
      },
    },
    Payouts: {
      getPendingCommissions: async () => {
        const res = await httpClient.get('/admin/referrals/commission/payout');
        return res.data;
      },
      releaseCommissions: async (data = {}) => {
        const res = await httpClient.post('/admin/referrals/commission/payout', data);
        return res.data;
      }
    }
  },
};

export const base44 = apiClient;
export default apiClient;